
// import { useEffect, useMemo, useState } from 'react';
// import AppLayout from '@/components/feature/AppLayout';
// import ConfirmDialog from '@/components/feature/ConfirmDialog';
// import { useToast } from '@/contexts/ToastContext';
// import { formatDateIST } from '@/utils/format';
// import { useAuthStore } from '@/stores/authStore';
// import { useRolesStore } from '@/stores/rolesStore';
// import type { AppUser, ModuleKey, ModuleAction, AssignedWarehouse, RolePermissions } from '@/types/shared';
// import { userService } from '@/services/userService';
// import { getAllWarehouses } from '@/api/warehouse.api';
// import type { UserDTO } from '@/api/types';
// import UserModal from './components/UserModal';
// import PermissionOverviewModal from './components/PermissionOverviewModal';
// import { MODULES } from '@/utils/permissions';
// import { useAuth } from '@/contexts/AuthContext';
// import { overridesToJsonb, jsonbToOverrides } from '@/utils/permissionHelpers';
// import { useWarehouseStore } from '@/stores/warehouseStore';

// const TYPE_BADGE: Record<string, string> = {
//   OFFICE: 'bg-sky-50 text-sky-700',
//   FACTORY: 'bg-amber-50 text-amber-700',
//   STORE: 'bg-green-50 text-green-700',
//   GODOWN: 'bg-violet-50 text-violet-700',
//   BRANCH: 'bg-indigo-50 text-indigo-700',
//   TRANSIT: 'bg-slate-100 text-slate-600',
// };

// interface UserFormData {
//   name: string;
//   email: string;
//   phone: string;
//   password: string;
//   roleId: string;
//   roleName: string;
//   isSuperAdmin: boolean;
//   assignedWarehouses: AssignedWarehouse[];
//   permissions: Partial<Record<ModuleKey, Partial<Record<ModuleAction, boolean>>>>;
//   additionalControlsOverrides: Partial<Record<string, boolean>>;
//   isActive: boolean;
// }

// interface WarehouseOption {
//   id: string;
//   name: string;
//   type: string;
//   isActive: boolean;
// }

// export default function UsersPage() {
//   const toast = useToast();
//   const { user: currentUser } = useAuthStore();
//   const { roles, fetchActiveRoles } = useRolesStore();

//   const [rawUsers, setRawUsers] = useState<UserDTO[]>([]);
//   const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [search, setSearch] = useState('');
//   const [roleFilter, setRoleFilter] = useState<string>('ALL');
//   const [modal, setModal] = useState<{
//     open: boolean;
//     editing: AppUser | null;
//     openAtSection?: 'basic' | 'role' | 'warehouses' | 'overrides'
//   }>({ open: false, editing: null });

//   const [deactivateConfirm, setDeactivateConfirm] = useState<AppUser | null>(null);
//   const [permissionsModal, setPermissionsModal] = useState<AppUser | null>(null);
//   const { hasPermission, hasControl } = useAuth();
//   const { selectedWarehouseId, selectedWarehouseName, setSelectedWarehouse } = useWarehouseStore();
//   const canCreateUser = hasPermission(MODULES.USERS, 'create') || hasControl("manageUserPermissions");
//   const canEditUser = hasPermission(MODULES.USERS, 'edit') || hasControl("manageUserPermissions");
//   const canDeleteUser = hasPermission(MODULES.USERS, 'delete') || hasControl("manageUserPermissions");

//   const users = useMemo<AppUser[]>(() => {
//     const warehouseById = new Map(warehouseOptions.map((w) => [w.id, w]));

//     return rawUsers.map((u) => {
//       const normalizedUserRole = String(u.role || '').trim().toUpperCase().replace(/\s+/g, '_');
//       const matchedRole = roles.find(
//         (r) => r.id === u.roleId || r.name === u.role || r.id === u.role
//       );

//       const assignedWarehouses: AssignedWarehouse[] = (u.warehouseIds || []).map((warehouseId, idx) => {
//         const wh = warehouseById.get(warehouseId);
//         return {
//           warehouseId,
//           warehouseName: wh?.name || warehouseId,
//           warehouseType: (wh?.type || 'GODOWN') as AssignedWarehouse['warehouseType'],
//           isPrimary: idx === 0,
//         };
//       });

//       // Calculate Effective Permissions and Controls
//       return {
//         id: u.id,
//         name: u.name,
//         email: u.email,
//         phone: u.phone || '',
//         roleId: u.roleId || matchedRole?.id || u.role,
//         roleName: normalizedUserRole === 'SUPER_ADMIN' ? 'Super Admin' : u.roleName || matchedRole?.name || u.role,
//         rolePermissions: matchedRole?.permissions || (u.rolePermissions as any),
//         roleAdditionalControls: matchedRole?.additionalControls,
//         isSuperAdmin: normalizedUserRole === 'SUPER_ADMIN',
//         assignedWarehouses,
//         permissionOverrides: jsonbToOverrides(u.permissions as Record<string, Record<string, boolean>>),
//         // Important: Store current state, Modal handles the delta logic
//         additionalControls: (u.additionalControls ?? {}) as Record<string, boolean>,
//         isActive: u.isActive,
//         lastLoginAt: u.lastActiveAt || null,
//         createdAt: u.createdAt,
//       };
//     });
//   }, [rawUsers, roles, warehouseOptions]);

//   const loadUsers = async () => {
//     const response = await userService.list({ page: 1, limit: 200 });
//     setRawUsers(response.items || []);
//   };

//   const loadWarehouses = async () => {
//     const response = await getAllWarehouses();
//     const rows = response.data || [];
//     setWarehouseOptions(rows.map((w) => ({
//       id: w.id,
//       name: w.name,
//       type: w.type,
//       isActive: w.is_active,
//     })));
//   };

//   useEffect(() => {
//     const initialize = async () => {
//       setLoading(true);
//       try {
//         await fetchActiveRoles();
//         await Promise.all([loadUsers(), loadWarehouses()]);
//       } catch (error) {
//         toast.error('Failed to load users data');
//       } finally {
//         setLoading(false);
//       }
//     };
//     void initialize();
//   }, [fetchActiveRoles]);

//   const filtered = users.filter((u) => {
//     const q = search.toLowerCase();
//     const matchSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.phone?.includes(q);
//     const matchRole = roleFilter === 'ALL' || (roleFilter === 'SUPER_ADMIN' ? u.isSuperAdmin : u.roleId === roleFilter);
//     const matchWarehouse = u.isSuperAdmin ? u : u.assignedWarehouses?.some((w) => w.warehouseId === selectedWarehouseId);

//     console.log('selectedWarehouseId===>', users.map((u) => u), matchWarehouse, selectedWarehouseId, selectedWarehouseName)

//     return matchSearch && matchRole;
//   });

//   const handleSave = async (data: UserFormData) => {
//     try {
//       const selectedDbRole = roles.find((r) => r.id === data.roleId);
//       const role = data.isSuperAdmin ? 'SUPER_ADMIN' : (selectedDbRole?.name || data.roleName.trim());
//       const warehouseIds = data.assignedWarehouses.map((w) => w.warehouseId);

//       // Convert UI matrix back to JSONB for backend
//       const permissions = overridesToJsonb(data.permissions);

//       const payload = {
//         name: data.name,
//         email: data.email,
//         phone: data.phone,
//         role,
//         roleId: data.roleId,
//         warehouseIds,
//         permissions, // Overrides
//         additionalControls: data.additionalControlsOverrides, // Overrides only
//         isActive: data.isActive,
//         ...(data.password ? { password: data.password } : {}),
//       };

//       if (modal.editing) {
//         await userService.update(modal.editing.id, payload);
//         toast.success(`User updated successfully`);
//       } else {
//         await userService.create(payload);
//         toast.success(`User created successfully`);
//       }

//       await loadUsers();
//       setModal({ open: false, editing: null });
//     } catch (error: any) {
//       toast.error(error.message || 'Failed to save user');
//     }
//   };

//   const handleDeactivate = async () => {
//     if (!deactivateConfirm) return;
//     try {
//       await userService.toggleActive(deactivateConfirm.id, !deactivateConfirm.isActive);
//       await loadUsers();
//       toast.success(`User status updated`);
//       setDeactivateConfirm(null);
//     } catch (error) {
//       toast.error('Failed to update user status');
//     }
//   };

//   const stats = {
//     total: users.length,
//     active: users.filter((u) => u.isActive).length,
//     superAdmins: users.filter((u) => u.isSuperAdmin).length,
//     withOverrides: users.filter((u) => Object.keys(u.permissionOverrides ?? {}).length > 0).length,
//   };

//   return (
//     <AppLayout>
//       <div className="p-6 space-y-5">
//         {/* Header and Stats */}
//         <div className="flex items-center justify-between">
//           <div>
//             <h2 className="text-xl font-bold text-[#1e293b]">Users & Access Control</h2>
//             <p className="text-sm text-[#64748b] mt-0.5">Manage team members and permissions</p>
//           </div>
//           {canCreateUser && (
//             <button onClick={() => setModal({ open: true, editing: null })} className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700">
//               <i className="ri-user-add-line" /> Add User
//             </button>
//           )}
//         </div>

//         <div className="grid grid-cols-4 gap-4">
//           {[
//             { label: 'Total Users', value: stats.total, icon: 'ri-team-line', bg: 'bg-indigo-50', color: 'text-[#4f46e5]' },
//             { label: 'Active', value: stats.active, icon: 'ri-checkbox-circle-line', bg: 'bg-green-50', color: 'text-green-600' },
//             { label: 'Super Admins', value: stats.superAdmins, icon: 'ri-shield-star-line', bg: 'bg-amber-50', color: 'text-amber-600' },
//             { label: 'Custom Overrides', value: stats.withOverrides, icon: 'ri-key-line', bg: 'bg-rose-50', color: 'text-rose-600' },
//           ].map((c) => (
//             <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
//               <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${c.bg}`}>
//                 <i className={`${c.icon} ${c.color} text-lg`} />
//               </div>
//               <div>
//                 <p className="text-2xl font-bold text-[#1e293b]">{c.value}</p>
//                 <p className="text-xs text-[#64748b]">{c.label}</p>
//               </div>
//             </div>
//           ))}
//         </div>

//         {/* Filter Bar */}
//         <div className="flex items-center gap-3 flex-wrap">
//           <div className="relative flex-1 max-w-xs">
//             <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm" />
//             <input
//               type="text" value={search}
//               onChange={(e) => setSearch(e.target.value)}
//               placeholder="Search users..."
//               className="w-full h-9 pl-9 pr-4 rounded-lg border border-[#e2e8f0] text-sm"
//             />
//           </div>
//           <div className="flex items-center gap-1 bg-[#f1f5f9] rounded-lg p-1">
//             <button onClick={() => setRoleFilter('ALL')} className={`h-7 px-3 rounded-md text-xs font-semibold ${roleFilter === 'ALL' ? 'bg-white shadow-sm' : 'text-[#64748b]'}`}>All</button>
//             <button onClick={() => setRoleFilter('SUPER_ADMIN')} className={`h-7 px-3 rounded-md text-xs font-semibold ${roleFilter === 'SUPER_ADMIN' ? 'bg-white shadow-sm' : 'text-[#64748b]'}`}>Super Admin</button>
//             {roles.filter(r => r.name !== 'SUPER_ADMIN').map(r => (
//               <button key={r.id} onClick={() => setRoleFilter(r.id)} className={`h-7 px-3 rounded-md text-xs font-semibold ${roleFilter === r.id ? 'bg-white shadow-sm' : 'text-[#64748b]'}`}>{r.name}</button>
//             ))}
//           </div>
//         </div>

//         {/* Users Table */}
//         <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden overflow-x-auto">
//           <table className="w-full min-w-[1000px] text-sm text-left">
//             <thead className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[#64748b] font-semibold uppercase text-xs">
//               <tr>
//                 <th className="px-4 py-3">User</th>
//                 <th className="px-4 py-3">Role</th>
//                 <th className="px-4 py-3">Warehouses</th>
//                 <th className="px-4 py-3">Status</th>
//                 <th className="px-4 py-3 text-right">Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filtered.map((u) => (
//                 <tr key={u.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]">
//                   <td className="px-4 py-3">
//                     <div className="flex items-center gap-3">
//                       <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-xs">
//                         {u.name.charAt(0)}
//                       </div>
//                       <div>
//                         <div className="font-medium text-[#1e293b]">{u.name}</div>
//                         <div className="text-xs text-[#64748b]">{u.email}</div>
//                       </div>
//                     </div>
//                   </td>
//                   <td className="px-4 py-3">
//                     <span className={`px-2 py-1 rounded-full text-xs font-semibold ${u.isSuperAdmin ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'}`}>
//                       {u.roleName}
//                     </span>
//                   </td>
//                   <td className="px-4 py-3">
//                     {u.isSuperAdmin ? 'All Access' : `${u.assignedWarehouses.length} Warehouses`}
//                   </td>
//                   <td className="px-4 py-3">
//                     <span className={`px-2 py-1 rounded-full text-xs font-semibold ${u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
//                       {u.isActive ? 'Active' : 'Inactive'}
//                     </span>
//                   </td>
//                   <td className="px-4 py-3 text-right">
//                     <div className="flex justify-end gap-2">
//                       {canEditUser && (<button onClick={() => setModal({ open: true, editing: u })} className="p-1 hover:text-indigo-600"><i className="ri-edit-line" /></button>)}
//                       <button onClick={() => setPermissionsModal(u)} className="p-1 hover:text-amber-600"><i className="ri-key-line" /></button>
//                       {canDeleteUser && (<button onClick={() => setDeactivateConfirm(u)} className="p-1 hover:text-red-600"><i className="ri-user-forbid-line" /></button>)}
//                     </div>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       <UserModal
//         open={modal.open}
//         editing={modal.editing}
//         roles={roles}
//         warehouses={warehouseOptions}
//         existingUsers={users}
//         onSave={handleSave}
//         onClose={() => setModal({ open: false, editing: null })}
//         openAtSection={modal.openAtSection}
//       />

//       {permissionsModal && (
//         <PermissionOverviewModal
//           user={permissionsModal}
//           role={roles.find((r) => r.id === permissionsModal.roleId) ?? null}
//           onClose={() => setPermissionsModal(null)}
//           onEditPermissions={() => {
//             const u = permissionsModal;
//             setPermissionsModal(null);
//             setModal({ open: true, editing: u, openAtSection: 'overrides' });
//           }}
//         />
//       )}

//       <ConfirmDialog
//         open={!!deactivateConfirm}
//         title={deactivateConfirm?.isActive ? 'Deactivate User' : 'Activate User'}
//         message={`Are you sure you want to change status for ${deactivateConfirm?.name}?`}
//         onConfirm={handleDeactivate}
//         onCancel={() => setDeactivateConfirm(null)}
//       />
//     </AppLayout>
//   );
// }

import { useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { useRolesStore } from '@/stores/rolesStore';
import type { AppUser, ModuleKey, ModuleAction, AssignedWarehouse } from '@/types/shared';
import { userService } from '@/services/userService';
import { getAllWarehouses } from '@/api/warehouse.api';
import type { UserDTO } from '@/api/types';
import UserModal from './components/UserModal';
import PermissionOverviewModal from './components/PermissionOverviewModal';
import { MODULES } from '@/utils/permissions';
import { useAuth } from '@/contexts/AuthContext';
import { overridesToJsonb, jsonbToOverrides } from '@/utils/permissionHelpers';
import { useWarehouseStore } from '@/stores/warehouseStore';

const TYPE_BADGE: Record<string, string> = {
  OFFICE: 'bg-sky-50 text-sky-700',
  FACTORY: 'bg-amber-50 text-amber-700',
  STORE: 'bg-green-50 text-green-700',
  GODOWN: 'bg-violet-50 text-violet-700',
  BRANCH: 'bg-indigo-50 text-indigo-700',
  TRANSIT: 'bg-slate-100 text-slate-600',
};

interface UserFormData {
  name: string;
  email: string;
  phone: string;
  password: string;
  roleId: string;
  roleName: string;
  isSuperAdmin: boolean;
  assignedWarehouses: AssignedWarehouse[];
  permissions: Partial<Record<ModuleKey, Partial<Record<ModuleAction, boolean>>>>;
  additionalControlsOverrides: Partial<Record<string, boolean>>;
  isActive: boolean;
}

interface WarehouseOption {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
}

export default function UsersPage() {
  const toast = useToast();
  const { roles, fetchActiveRoles } = useRolesStore();

  // ── Global warehouse context (set by AppLayout header switcher) ──
  const { selectedWarehouseId } = useWarehouseStore();

  const [rawUsers, setRawUsers] = useState<UserDTO[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');

  // warehouseFilter starts as 'INIT' — we resolve it once isSuperAdmin + selectedWarehouseId are known
  const [warehouseFilter, setWarehouseFilter] = useState<string>('INIT');

  const [modal, setModal] = useState<{
    open: boolean;
    editing: AppUser | null;
    openAtSection?: 'basic' | 'role' | 'warehouses' | 'overrides';
  }>({ open: false, editing: null });

  const [deactivateConfirm, setDeactivateConfirm] = useState<AppUser | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AppUser | null>(null);
  const [resetConfirm, setResetConfirm] = useState<AppUser | null>(null);
  const [resetResult, setResetResult] = useState<{ user: AppUser; tempPassword: string } | null>(null);
  const [copiedReset, setCopiedReset] = useState(false);
  const [permissionsModal, setPermissionsModal] = useState<AppUser | null>(null);

  const { hasPermission, hasControl, user: currentUser } = useAuth();

  // ── Derive isSuperAdmin from auth context ──────────────────────────────────
  const isSuperAdmin = useMemo(() => {
    if (!currentUser) return false;
    const role = String(currentUser.role || '').trim().toUpperCase().replace(/\s+/g, '_');
    return role === 'SUPER_ADMIN' || role === 'SUB_ADMIN';
  }, [currentUser]);

  // ── Resolve initial warehouse filter once we know the user's role ──────────
  // 'INIT' → 'ALL' for Super Admin, or selectedWarehouseId for regular users.
  // After this, the user can change it freely via the dropdown.
  useEffect(() => {
    if (warehouseFilter !== 'INIT') return; // already resolved
    if (!currentUser) return;               // wait for auth

    if (isSuperAdmin) {
      setWarehouseFilter('ALL');
    } else if (selectedWarehouseId) {
      setWarehouseFilter(selectedWarehouseId);
    } else {
      setWarehouseFilter('ALL');
    }
  }, [isSuperAdmin, selectedWarehouseId, currentUser, warehouseFilter]);

  const canCreateUser = hasPermission(MODULES.USERS, 'create') || hasControl('manageUserPermissions');
  const canEditUser   = hasPermission(MODULES.USERS, 'edit')   || hasControl('manageUserPermissions');
  const canDeleteUser = hasPermission(MODULES.USERS, 'delete') || hasControl('manageUserPermissions');

  // ─── Derived user list ─────────────────────────────────────────────────────
  const users = useMemo<AppUser[]>(() => {
    const warehouseById = new Map(warehouseOptions.map((w) => [w.id, w]));

    return rawUsers.map((u) => {
      const normalizedUserRole = String(u.role || '').trim().toUpperCase().replace(/\s+/g, '_');
      const matchedRole = roles.find(
        (r) => r.id === u.roleId || r.name === u.role || r.id === u.role,
      );

      const assignedWarehouses: AssignedWarehouse[] = (u.warehouseIds || []).map(
        (warehouseId, idx) => {
          const wh = warehouseById.get(warehouseId);
          return {
            warehouseId,
            warehouseName: wh?.name || warehouseId,
            warehouseType: (wh?.type || 'GODOWN') as AssignedWarehouse['warehouseType'],
            isPrimary: idx === 0,
          };
        },
      );

      return {
        id: u.id,
        name: u.name,
        email: u.email,
        phone: u.phone || '',
        roleId: u.roleId || matchedRole?.id || u.role,
        roleName:
          normalizedUserRole === 'SUPER_ADMIN'
            ? 'Super Admin'
            : normalizedUserRole === 'SUB_ADMIN'
            ? 'Sub Admin'
            : u.roleName || matchedRole?.name || u.role,
        rolePermissions: matchedRole?.permissions || (u.rolePermissions as any),
        roleAdditionalControls: matchedRole?.additionalControls,
        isSuperAdmin: normalizedUserRole === 'SUPER_ADMIN',
        isSubAdmin: normalizedUserRole === 'SUB_ADMIN',
        assignedWarehouses,
        permissionOverrides: jsonbToOverrides(
          u.permissions as Record<string, Record<string, boolean>>,
        ),
        additionalControls: (u.additionalControls ?? {}) as Record<string, boolean>,
        isActive: u.isActive,
        lastLoginAt: u.lastActiveAt || null,
        createdAt: u.createdAt,
      };
    });
  }, [rawUsers, roles, warehouseOptions]);

  // ─── Filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    // Don't filter until initial warehouse is resolved
    if (warehouseFilter === 'INIT') return [];

    const q = search.toLowerCase();

    return users.filter((u) => {
      const matchSearch =
        !q ||
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.phone?.includes(q);

      const matchRole =
        roleFilter === 'ALL' ||
        (roleFilter === 'SUPER_ADMIN' ? u.isSuperAdmin : u.roleId === roleFilter);

      const matchStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' ? u.isActive : !u.isActive);

      // Warehouse filter rules:
      //   1. 'ALL' selected → show everyone
      //   2. Logged-in user is Super Admin → always show all (they bypass restrictions)
      //   3. The user row itself is Super Admin → always show them (they're global)
      //   4. Otherwise → user must be assigned to the selected warehouse
      const matchWarehouse =
        warehouseFilter === 'ALL' ||
        // isSuperAdmin ||
        u.isSuperAdmin ||
        u.assignedWarehouses.some((w) => w.warehouseId === warehouseFilter);

      return matchSearch && matchRole && matchStatus && matchWarehouse;
    });
  }, [users, search, roleFilter, statusFilter, warehouseFilter, isSuperAdmin]);

  // ─── Warehouse dropdown options ────────────────────────────────────────────
  // Super Admin → all active warehouses + "All" option
  // Regular user → only their assigned warehouse(s) — derived from the loaded users list
  const dropdownWarehouses = useMemo(() => {
    if (isSuperAdmin) {
      return warehouseOptions.filter((w) => w.isActive);
    }
    // Find the current user's own AppUser record to get their assigned warehouses
    const meAsUser = users.find((u) => u.id === (currentUser as any)?.id);
    const myWarehouseIds = new Set(
      meAsUser?.assignedWarehouses.map((w) => w.warehouseId) ?? [selectedWarehouseId].filter(Boolean),
    );
    return warehouseOptions.filter((w) => w.isActive && myWarehouseIds.has(w.id));
  }, [isSuperAdmin, warehouseOptions, users, currentUser, selectedWarehouseId]);

  // ─── Data loaders ──────────────────────────────────────────────────────────
  const loadUsers = async () => {
    const response = await userService.list({ page: 1, limit: 200 });
    setRawUsers(response.items || []);
  };

  const loadWarehouses = async () => {
    const response = await getAllWarehouses();
    const rows = response.data || [];
    setWarehouseOptions(
      rows.map((w) => ({ id: w.id, name: w.name, type: w.type, isActive: w.is_active })),
    );
  };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await fetchActiveRoles();
        await Promise.all([loadUsers(), loadWarehouses()]);
      } catch {
        toast.error('Failed to load users data');
      } finally {
        setLoading(false);
      }
    };
    void initialize();
  }, [fetchActiveRoles]);

  // ─── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (data: UserFormData) => {
    try {
      const selectedDbRole = roles.find((r) => r.id === data.roleId);
      const role = data.isSuperAdmin
        ? 'SUPER_ADMIN'
        : selectedDbRole?.name || data.roleName.trim();
      const warehouseIds = data.assignedWarehouses.map((w) => w.warehouseId);
      const permissions = overridesToJsonb(data.permissions);

      const payload = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        role,
        roleId: data.roleId,
        warehouseIds,
        permissions,
        additionalControls: data.additionalControlsOverrides,
        isActive: data.isActive,
        ...(data.password ? { password: data.password } : {}),
      };

      if (modal.editing) {
        await userService.update(modal.editing.id, payload);
        toast.success('User updated successfully');
        await loadUsers();
        setModal({ open: false, editing: null });
      } else {
        const created = await userService.create(payload);
        toast.success('User created successfully');
        await loadUsers();
        if (created?.temporaryPassword) {
          return { temporaryPassword: created.temporaryPassword };
        }
        setModal({ open: false, editing: null });
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save user');
    }
  };

  // ─── Reset password ────────────────────────────────────────────────────────
  const handleResetPassword = async () => {
    if (!resetConfirm) return;
    try {
      const res = await userService.resetPassword(resetConfirm.id);
      setResetResult({ user: resetConfirm, tempPassword: res.temporaryPassword });
      setResetConfirm(null);
      toast.success('Password reset successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset password');
    }
  };

  // ─── Toggle active ─────────────────────────────────────────────────────────
  const handleDeactivate = async () => {
    if (!deactivateConfirm) return;
    try {
      await userService.toggleActive(deactivateConfirm.id, !deactivateConfirm.isActive);
      await loadUsers();
      toast.success('User status updated');
      setDeactivateConfirm(null);
    } catch {
      toast.error('Failed to update user status');
    }
  };

  // ─── Delete user ───────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await userService.delete(deleteConfirm.id);
      await loadUsers();
      toast.success('User deleted successfully');
      setDeleteConfirm(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('ALL');
    setStatusFilter('ALL');
    setWarehouseFilter(isSuperAdmin ? 'ALL' : selectedWarehouseId || 'ALL');
  };

  // ─── Stats (always based on full list, not filtered view) ─────────────────
  const stats = {
    total: users.length,
    active: users.filter((u) => u.isActive).length,
    superAdmins: users.filter((u) => u.isSuperAdmin).length,
    withOverrides: users.filter((u) => Object.keys(u.permissionOverrides ?? {}).length > 0).length,
  };

  const activeWarehouseName = warehouseOptions.find((w) => w.id === warehouseFilter)?.name;
  const hasActiveFilters =
    search || roleFilter !== 'ALL' || statusFilter !== 'ALL' ||
    (isSuperAdmin ? warehouseFilter !== 'ALL' : warehouseFilter !== selectedWarehouseId);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <div className="p-6 h-[calc(100vh-4rem)] flex flex-col gap-5">
        <div className="flex flex-col gap-5 shrink-0">
        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#1e293b]">Users & Access Control</h2>
            <p className="text-sm text-[#64748b] mt-0.5">Manage team members and permissions</p>
          </div>
          {canCreateUser && (
            <button
              onClick={() => setModal({ open: true, editing: null })}
              className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
            >
              <i className="ri-user-add-line" /> Add User
            </button>
          )}
        </div>

        {/* ── Stats Cards ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Users',      value: stats.total,         icon: 'ri-team-line',            bg: 'bg-indigo-50', color: 'text-[#4f46e5]' },
            { label: 'Active',           value: stats.active,        icon: 'ri-checkbox-circle-line', bg: 'bg-green-50',  color: 'text-green-600'  },
            { label: 'Super Admins',     value: stats.superAdmins,   icon: 'ri-shield-star-line',     bg: 'bg-amber-50',  color: 'text-amber-600'  },
            { label: 'Custom Overrides', value: stats.withOverrides, icon: 'ri-key-line',             bg: 'bg-rose-50',   color: 'text-rose-600'   },
          ].map((c) => (
            <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${c.bg}`}>
                <i className={`${c.icon} ${c.color} text-lg`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e293b]">{c.value}</p>
                <p className="text-xs text-[#64748b]">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter Bar ── */}
        <div className="flex flex-wrap items-center gap-3">

          {/* Search */}
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full h-9 pl-9 pr-4 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>

          {/* Warehouse filter dropdown */}
          <div className="relative">
            <i className="ri-store-2-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm pointer-events-none" />
            <select
              value={warehouseFilter === 'INIT' ? '' : warehouseFilter}
              onChange={(e) => setWarehouseFilter(e.target.value)}
              className="h-9 pl-8 pr-8 rounded-lg border border-[#e2e8f0] text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              {/* "All Warehouses" option — only Super Admin gets this */}
              {isSuperAdmin && (
                <option value="ALL">All Warehouses</option>
              )}
              {/* List of warehouses the current user is allowed to see */}
              {dropdownWarehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-[#94a3b8] pointer-events-none" />
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-1 bg-[#f1f5f9] rounded-lg p-1">
            {(['ALL', 'ACTIVE', 'INACTIVE'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                  statusFilter === s ? 'bg-white shadow-sm text-[#1e293b]' : 'text-[#64748b]'
                }`}
              >
                {s === 'ALL' ? 'All Status' : s.charAt(0) + s.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          {/* Role filter */}
          <div className="flex items-center gap-1 bg-[#f1f5f9] rounded-lg p-1 flex-wrap">
            <button
              onClick={() => setRoleFilter('ALL')}
              className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                roleFilter === 'ALL' ? 'bg-white shadow-sm text-[#1e293b]' : 'text-[#64748b]'
              }`}
            >
              All Roles
            </button>
            <button
              onClick={() => setRoleFilter('SUPER_ADMIN')}
              className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                roleFilter === 'SUPER_ADMIN' ? 'bg-white shadow-sm text-[#1e293b]' : 'text-[#64748b]'
              }`}
            >
              Super Admin
            </button>
            {roles
              .filter((r) => r.name !== 'SUPER_ADMIN')
              .map((r) => (
                <button
                  key={r.id}
                  onClick={() => setRoleFilter(r.id)}
                  className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                    roleFilter === r.id ? 'bg-white shadow-sm text-[#1e293b]' : 'text-[#64748b]'
                  }`}
                >
                  {r.name}
                </button>
              ))}
          </div>
        </div>

        {/* ── Result summary ── */}
        <div className="flex items-center justify-between text-xs text-[#64748b]">
          <span className="flex items-center gap-2 flex-wrap">
            Showing{' '}
            <span className="font-semibold text-[#1e293b]">{filtered.length}</span> of{' '}
            <span className="font-semibold text-[#1e293b]">{users.length}</span> users

            {/* Current warehouse context pill */}
            {warehouseFilter !== 'ALL' && warehouseFilter !== 'INIT' && activeWarehouseName && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-semibold">
                <i className="ri-store-2-line text-[10px]" />
                {activeWarehouseName}
              </span>
            )}
            {warehouseFilter === 'ALL' && isSuperAdmin && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">
                <i className="ri-shield-star-line text-[10px]" /> All warehouses (Admin)
              </span>
            )}
          </span>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-indigo-600 hover:underline font-medium"
            >
              Clear filters
            </button>
          )}
        </div>

        </div>
        {/* ── Table ── */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-auto hover-scrollbar rounded-xl">
          {loading || warehouseFilter === 'INIT' ? (
            <div className="flex items-center justify-center py-16 gap-2 text-[#64748b]">
              <i className="ri-loader-4-line animate-spin text-xl" />
              <span className="text-sm">Loading users…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-[#94a3b8]">
              <i className="ri-user-search-line text-3xl" />
              <p className="text-sm font-medium">No users found for this warehouse</p>
              <button
                onClick={clearFilters}
                className="text-indigo-600 text-xs hover:underline mt-1"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <table className="w-full min-w-[900px] text-sm text-left">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[#64748b] font-semibold uppercase text-xs sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Warehouses</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Last Active</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors">

                    {/* User */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-[#1e293b]">{u.name}</div>
                          <div className="text-xs text-[#64748b]">{u.email}</div>
                          {u.phone && <div className="text-xs text-[#94a3b8]">{u.phone}</div>}
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          u.isSuperAdmin ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {u.isSuperAdmin && <i className="ri-shield-star-line mr-1" />}
                        {u.roleName}
                      </span>
                    </td>

                    {/* Warehouses */}
                    <td className="px-4 py-3">
                      {u.isSuperAdmin ? (
                        <span className="text-xs text-amber-600 font-semibold flex items-center gap-1">
                          <i className="ri-shield-star-line" /> All Warehouses
                        </span>
                      ) : u.assignedWarehouses.length === 0 ? (
                        <span className="text-xs text-[#94a3b8]">None assigned</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {u.assignedWarehouses.slice(0, 3).map((w) => (
                            <span
                              key={w.warehouseId}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                TYPE_BADGE[w.warehouseType] ?? 'bg-slate-50 text-slate-600'
                              } ${w.isPrimary ? 'ring-1 ring-indigo-300' : ''}`}
                              title={w.isPrimary ? 'Primary warehouse' : undefined}
                            >
                              {w.warehouseName}
                            </span>
                          ))}
                          {u.assignedWarehouses.length > 3 && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                              +{u.assignedWarehouses.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* Last Active */}
                    <td className="px-4 py-3 text-xs text-[#64748b]">
                      {u.lastLoginAt ? (
                        new Date(u.lastLoginAt).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })
                      ) : (
                        <span className="text-[#cbd5e1]">Never</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {canEditUser && (
                          <button
                            onClick={() => setModal({ open: true, editing: u })}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-indigo-50 hover:text-indigo-600 text-[#64748b] transition-colors"
                            title="Edit user"
                          >
                            <i className="ri-edit-line" />
                          </button>
                        )}
                        {canEditUser && (
                          <button
                            onClick={() => setResetConfirm(u)}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-amber-50 hover:text-amber-600 text-[#64748b] transition-colors"
                            title="Reset password"
                          >
                            <i className="ri-lock-password-line" />
                          </button>
                        )}
                        <button
                          onClick={() => setPermissionsModal(u)}
                          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-amber-50 hover:text-amber-600 text-[#64748b] transition-colors"
                          title="View permissions"
                        >
                          <i className="ri-key-line" />
                        </button>
                        {canDeleteUser && u.id !== currentUser?.id && !u.isSuperAdmin && (
                          <button
                            onClick={() => setDeactivateConfirm(u)}
                            className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                              u.isActive
                                ? 'hover:bg-red-50 hover:text-red-600 text-[#64748b]'
                                : 'hover:bg-green-50 hover:text-green-600 text-[#64748b]'
                            }`}
                            title={u.isActive ? 'Deactivate user' : 'Activate user'}
                          >
                            <i className={u.isActive ? 'ri-user-forbid-line' : 'ri-user-follow-line'} />
                          </button>
                        )}
                        {canDeleteUser && u.id !== currentUser?.id && !u.isSuperAdmin && (
                          <button
                            onClick={() => setDeleteConfirm(u)}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 hover:text-red-600 text-[#64748b] transition-colors"
                            title="Delete user"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <UserModal
        open={modal.open}
        editing={modal.editing}
        roles={roles}
        warehouses={isSuperAdmin?warehouseOptions:warehouseOptions.filter((w)=>w.id===selectedWarehouseId)}
        existingUsers={users}
        onSave={handleSave}
        onClose={() => setModal({ open: false, editing: null })}
        openAtSection={modal.openAtSection}
        isCreatorSuperAdmin={String(currentUser?.role || '').trim().toUpperCase().replace(/\s+/g, '_') === 'SUPER_ADMIN'}
      />

      {permissionsModal && (
        <PermissionOverviewModal
          user={permissionsModal}
          role={roles.find((r) => r.id === permissionsModal.roleId) ?? null}
          onClose={() => setPermissionsModal(null)}
          onEditPermissions={() => {
            const u = permissionsModal;
            setPermissionsModal(null);
            setModal({ open: true, editing: u, openAtSection: 'overrides' });
          }}
        />
      )}

      <ConfirmDialog
        open={!!deactivateConfirm}
        title={deactivateConfirm?.isActive ? 'Deactivate User' : 'Activate User'}
        message={`Are you sure you want to ${deactivateConfirm?.isActive ? 'deactivate' : 'activate'} ${deactivateConfirm?.name}?`}
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateConfirm(null)}
      />

      <ConfirmDialog
        open={!!deleteConfirm}
        title="Delete User"
        message={`Are you sure you want to permanently delete ${deleteConfirm?.name}? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
      />

      <ConfirmDialog
        open={!!resetConfirm}
        title="Reset User Password"
        message={`Are you sure you want to reset the password for ${resetConfirm?.name}? A temporary password will be generated and they will be required to change it upon next login.`}
        onConfirm={handleResetPassword}
        onCancel={() => setResetConfirm(null)}
      />

      {resetResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                <i className="ri-key-2-line text-xl" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Temporary Password Generated</h3>
                <p className="text-xs text-gray-500">{resetResult.user.name} ({resetResult.user.email})</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              Share securely; the user must change it on first login.
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700">Temporary Password</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={resetResult.tempPassword}
                  className="w-full font-mono text-sm px-3 py-2 border rounded-lg bg-gray-50 select-all"
                />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(resetResult.tempPassword);
                    setCopiedReset(true);
                    setTimeout(() => setCopiedReset(false), 2000);
                  }}
                  className="px-3 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-1.5 shrink-0"
                >
                  <i className={copiedReset ? 'ri-check-line text-green-300' : 'ri-file-copy-line'} />
                  {copiedReset ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setResetResult(null);
                  setCopiedReset(false);
                }}
                className="px-4 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}